import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarkdownRender from "@/components/MarkdownRender";
import { LEGAL_DOCS, SIDEBAR_GROUPS } from "@/legal/content";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, AlertTriangle } from "lucide-react";

export default function Legal() {
  const { slug } = useParams();
  const doc = LEGAL_DOCS[slug];

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!doc) {
    return <Navigate to="/legal/terms-of-service" replace />;
  }

  return (
    <div className="min-h-screen bg-ec-bg" data-testid={`legal-page-${slug}`}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 grid lg:grid-cols-12 gap-10">
        {/* Sidebar */}
        <aside className="lg:col-span-3 lg:sticky lg:top-24 self-start" data-testid="legal-sidebar">
          <div className="border border-ec-line bg-white p-4">
            <div className="eyebrow mb-2">EthicCred Legal</div>
            {SIDEBAR_GROUPS.map((g) => (
              <div key={g.label} className="mt-4 first:mt-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-ec-ink-soft px-2">{g.label}</div>
                <ul className="mt-1">
                  {g.slugs.map((s) => {
                    const item = LEGAL_DOCS[s];
                    if (!item) return null;
                    const active = s === slug;
                    return (
                      <li key={s}>
                        <Link
                          to={`/legal/${s}`}
                          className={`block px-2 py-1.5 text-sm rounded-sm transition-colors ${
                            active
                              ? "bg-ec-ink text-white"
                              : "text-ec-ink hover:bg-ec-mute"
                          }`}
                          data-testid={`legal-nav-${s}`}
                        >
                          {item.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        {/* Content */}
        <article className="lg:col-span-9 bg-white border border-ec-line p-8 sm:p-12">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 eyebrow text-ec-emerald">
              <ShieldCheck className="w-3.5 h-3.5" /> {doc.category}
            </div>
            <div className="font-mono-plex text-xs text-ec-ink-soft">Last updated · {doc.lastUpdated}</div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3" data-testid="legal-title">
            {doc.title}
          </h1>

          {doc.placeholder && (
            <div
              className="mt-6 border border-ec-amber/40 bg-ec-amber/5 text-ec-ink text-sm p-4 flex items-start gap-3"
              data-testid="legal-placeholder-banner"
            >
              <AlertTriangle className="w-4 h-4 text-ec-amber mt-0.5 flex-shrink-0" />
              <div>
                <strong>Reference content.</strong> This page is an EthicCred‑aligned summary
                pending publication of the official, counsel‑reviewed policy text. The clauses
                below reflect EthicCred's current platform practice and will be replaced by the
                final policy document.
              </div>
            </div>
          )}

          <div className="mt-8 text-base leading-7">
            <MarkdownRender content={doc.content} />
          </div>

          <div className="mt-12 border-t border-ec-line pt-6 flex flex-wrap items-center justify-between gap-4">
            <Link to="/legal/contact" className="text-sm font-semibold text-ec-emerald underline underline-offset-4" data-testid="legal-contact-link">
              Contact the Legal Team →
            </Link>
            <Badge variant="outline" className="font-mono-plex">EthicCred v1.0 · {doc.lastUpdated}</Badge>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
}
